import React from "react";
import { StyleSheet, View, Modal } from "react-native";
import { MessageModel } from "common247";
import { MessageDetailView, type IMessageDetailViewProps } from "./MessageDetailView";

/**
 * Properties for the MessageDetailModal component.
 */
export interface IMessageDetailModalProps {
  /** The message object to inspect, or null if modal is closed */
  message: MessageModel | null;
  /** Visibility state of the modal */
  visible: boolean;
  /** Callback triggered when modal is dismissed */
  onClose: () => void;
  /** Callback triggered to mark message as read */
  onMarkRead: (messageId: string) => void;
  /** Optional callback triggered to mark message as unread */
  onMarkUnread?: (messageId: string) => void;
  /** Optional callback to reply to the sender or group */
  onReply?: (message: MessageModel) => void;
  /** Optional callback to move message to trash */
  onMoveToTrash?: (messageId: string) => void;
  /** Optional callback to restore message from trash */
  onRestore?: (messageId: string) => void;
}

/**
 * Modal dialog displaying the full contents and metadata of an internal message.
 * Delegates message layout and actions to MessageDetailView.
 */
export function MessageDetailModal({
  message,
  visible,
  onClose,
  onMarkRead,
  onMarkUnread,
  onReply,
  onMoveToTrash,
  onRestore,
}: IMessageDetailModalProps): React.JSX.Element | null {
  const handleMarkUnread = (messageId: string) => {
    onMarkUnread?.(messageId);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.modalBackdrop}>
        <View style={styles.modalCard}>
          <MessageDetailView
            message={message}
            onMarkRead={onMarkRead}
            onMarkUnread={handleMarkUnread}
            onReply={onReply}
            onMoveToTrash={onMoveToTrash}
            onRestore={onRestore}
            onClose={onClose}
            showCloseButton={true}
          />
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalBackdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.65)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  modalCard: {
    width: "100%",
    maxWidth: 600,
    maxHeight: "85%",
    borderRadius: 0,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 10,
    elevation: 8,
    overflow: "hidden",
  },
});
